import React, {useEffect, useMemo} from "react";
import {GlobalStyles} from "../../constants/styles";
import {formattedDateTime} from "../../util/date";
import useTranslation from "../../i18n/useTranslation";

const SMALL_DT_FORMAT = "LLL d '•' h:mm a";

export default function NotificationDetailsModal({open, onClose, row, timezone}) {
    const { t } = useTranslation();
    const n = row?.notification || {};
    const title = n?.title || "Notification";
    const message = n?.message || "";
    const typeKey = n?.type ? String(n.type).toLowerCase() : null;
    const type = typeKey ? t(`notificationTypes.${typeKey}`, {}, String(n.type)) : "—";
    const senderName = n?.sender?.fullName || "System";
    const createdAt = n?.createdAt;
    const externalLink = n?.externalLink || null;

    const createdLabel = useMemo(() => {
        return createdAt ? formattedDateTime(createdAt, timezone, "en", SMALL_DT_FORMAT) : "—";
    }, [createdAt, timezone]);

    useEffect(() => {
        if (!open) return;

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onEsc = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onEsc);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onEsc);
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <>
            <div className="kbola-notif-modal-overlay" onClick={onClose} role="presentation"/>

            <div className="kbola-notif-modal" role="dialog" aria-modal="true" aria-label="Notification details">
                <div className="kbola-notif-modal-head">
                    <div className="kbola-notif-modal-head-left">
            <span className="kbola-notif-modal-badge">
              <i className="fas fa-bell"/>
            </span>

                        <div className="kbola-notif-modal-head-text">
                            <div className="kbola-notif-modal-title" title={title}>{title}</div>
                            <div className="kbola-notif-modal-subtitle">
                                {createdLabel} • {senderName}
                            </div>
                        </div>
                    </div>

                    <button type="button" className="btn btn-light kbola-notif-modal-close" onClick={onClose}
                            title="Close">
                        <i className="fas fa-times"/>
                    </button>
                </div>

                <div className="kbola-notif-modal-body">
                    <div className="kbola-notif-modal-meta">
                        <span className="kbola-notif-modal-pill">
                          <i className="fas fa-tag mr-2"/>
                            {type}
                        </span>

                        {externalLink && (
                            <div className="kbola-notif-modal-external-link">
                                <a
                                    href={externalLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="kbola-external-link"
                                >
                                    <i className="fas fa-external-link-alt" aria-hidden="true"/>
                                    <span className="kbola-external-link-text">{externalLink}</span>
                                </a>
                            </div>
                        )}
                    </div>


                    <div className="kbola-notif-modal-message">
                        {message || "—"}
                    </div>
                </div>

                <div className="kbola-notif-modal-footer">
                    <button type="button" className="btn btn-dark kbola-notif-modal-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>

            <style>{`
        .kbola-notif-modal-overlay{
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.22);
          z-index: 1400;
        }

        .kbola-notif-modal{
          position: fixed;
          inset: auto;
          z-index: 1401;
          top: 90px;
          left: 50%;
          transform: translateX(-50%);
          width: min(720px, calc(100vw - 28px));
          max-height: calc(100vh - 120px);
          background: #fff;
          border-radius: 18px;
          border: 1px solid rgba(0,0,0,0.10);
          box-shadow: 0 26px 70px rgba(0,0,0,0.22);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          animation: kbolaNotifModalIn 140ms ease forwards;
        }

        @keyframes kbolaNotifModalIn{
          from { transform: translateX(-50%) translateY(10px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }

        .kbola-notif-modal-head{
          padding: 12px 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          background: linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.18), rgba(0,0,0,0));
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }

        .kbola-notif-modal-head-left{
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .kbola-notif-modal-badge{
          width: 34px;
          height: 34px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.95);
          border: 1px solid rgba(0,0,0,0.10);
          color: ${GlobalStyles.colors.woodsmoke};
          flex: 0 0 34px;
        }

        .kbola-notif-modal-head-text{ min-width: 0; }

        .kbola-notif-modal-title{
          font-size: 14px;
          font-weight: 950;
          color: #14151a;
          line-height: 1.1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 560px;
        }

        .kbola-notif-modal-subtitle{
          margin-top: 2px;
          font-size: 12.5px;
          font-weight: 800;
          color: rgba(20,21,26,0.55);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 560px;
        }

        .kbola-notif-modal-close{
          border-radius: 14px !important;
          padding: 10px 12px !important;
          font-weight: 900 !important;
        }

        .kbola-notif-modal-body{
          padding: 14px;
          overflow: auto;
          flex: 1 1 auto;
          min-height: 0;
        }

        .kbola-notif-modal-meta {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 10px;
        }


        .kbola-notif-modal-pill{
          display: inline-flex;
          align-items: center;
          border: 1px solid rgba(0,0,0,0.10);
          background: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.04);
          color: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.85);
          border-radius: 999px;
          padding: 7px 10px;
          font-size: 12px;
          font-weight: 950;
        }

        .kbola-notif-modal-message{
          font-size: 14px;
          font-weight: 750;
          color: rgba(20,21,26,0.82);
          line-height: 1.35rem;
          white-space: pre-wrap; /* keeps line breaks if message contains them */
          word-break: break-word;
        }
        
        .kbola-notif-modal-external-link {
          margin-left: auto;           /* push it to the right */
          display: inline-flex;
          align-items: center;
        }

        
        /* clickable pill-style link */
        .kbola-external-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        
          font-weight: 700;
          font-size: 13px;
          text-decoration: none;
          color: var(--kbs-primary, #00adff); /* canary */
        
          background: rgba(20, 21, 26, 0.88); /* woodsmoke-ish */
          padding: 8px 12px;
          border-radius: 999px;
          border: 1px solid rgba(var(--kbs-primary-rgb, 0,173,255), 0.35);
        
          cursor: pointer;
          transition:
            background-color 0.16s ease,
            transform 0.12s ease,
            box-shadow 0.16s ease;
        }
        
        /* icon inside the link */
        .kbola-external-link i {
          font-size: 12px;
          opacity: 0.9;
        }
        
        /* URL text handling: keep it readable but break nicely */
        .kbola-external-link-text {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 260px; /* tweak as needed */
        }
        
        /* Hover / focus states */
        .kbola-external-link:hover,
        .kbola-external-link:focus {
          background: ${GlobalStyles.colors.canary};
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
          transform: translateY(-1px);
          outline: none;
          color: ${GlobalStyles.colors.kBolaBlue};
        }
        
        .kbola-external-link:active {
          transform: translateY(0);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.24);
        }


        .kbola-notif-modal-footer{
          padding: 12px 14px;
          border-top: 1px solid rgba(0,0,0,0.06);
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          background: #fff;
          flex: 0 0 auto;
        }

        .kbola-notif-modal-btn{
          border-radius: 14px !important;
          font-weight: 900 !important;
          padding: 10px 12px !important;
        }

        @media (max-width: 768px){
          .kbola-notif-modal{
            top: 12px;
            max-height: calc(100vh - 24px);
          }
          .kbola-notif-modal-title,
          .kbola-notif-modal-subtitle{
            max-width: 72vw;
          }
        }
      `}</style>
        </>
    );
}
